import { ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useState } from "react";
import { ITask } from "../types";
import { Link } from "react-router-dom";
import ROUTES from "../../../configs/routesConfig";
import dayjs from "../../../configs/dayjs";
import { DEFAULT_TIME_FORMAT } from "../../../lib/constants";
import { TableActions } from "../components/TableActions";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Row } from "antd";
import WorkTasksService from "../../../services/workTasks";
import { useDownloadReport } from "../../../lib/hooks/useDownloadReport";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import { TableAssignColumn } from "../components/TableAssignColumn";

export const useTableColumns = (
  isClosed: boolean,
  setTaskIdToClose: React.Dispatch<React.SetStateAction<string | undefined>>,
  reload: () => void,
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { download } = useDownloadReport(setIsLoading);

  const onDownloadClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (!isLoading) {
        e.stopPropagation();
        download(WorkTasksService, e.currentTarget.id);
      }
    },
    [download, isLoading],
  );

  const { hasAccessByController } = usePermissions("WorkTasks");

  const columns: ColumnsType<ITask> = useMemo(
    () => [
      {
        title: "Task",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        width: "17%",
        sorter: true,
        render: (value: string) => (
          <Row className="align-center name-col">
            {isClosed && (
              <CheckCircleOutlined height={18} width={18} className="mr-3" />
            )}
            <span className="elipsis val">{value}</span>
          </Row>
        ),
      },
      {
        title: "Attachment",
        dataIndex: "fileName",
        key: "fileName",
        ellipsis: true,
        width: "18%",
        render: (value: string, record: ITask) => (
          <span
            id={record.id}
            className="elipsis ant-btn-link"
            onClick={onDownloadClick}
          >
            {value}
          </span>
        ),
      },
      {
        title: "Account #",
        dataIndex: "contractNumber",
        key: "contractNumber",
        ellipsis: true,
        width: "10%",
        sorter: true,
        render: (value: number, record: ITask) => (
          <Link to={`${ROUTES.ACCOUNTS}/${record.contractId}`} target="_blank">
            <span className="ant-btn-link">{value}</span>
          </Link>
        ),
      },
      {
        title: "Requested by",
        dataIndex: "createdBy",
        key: "createdBy",
        ellipsis: true,
        width: "16%",
        sorter: true,
      },
      {
        title: "Created Date",
        dataIndex: "sentAt",
        key: "sentAt",
        ellipsis: true,
        width: "14%",
        sorter: true,
        render: (val: string) => dayjs(val).format(DEFAULT_TIME_FORMAT),
      },
      {
        title: "Assignee",
        dataIndex: "assign",
        key: "assign",
        ellipsis: true,
        width: "14%",
        sorter: true,
        render: (_: unknown, record: ITask) => {
          const { assignedToFullName, id } = record;

          return (
            <TableAssignColumn
              isClosed={isClosed}
              id={id}
              assignedToFullName={assignedToFullName}
              reload={reload}
            />
          );
        },
      },
      {
        dataIndex: "actions",
        key: "actions",
        width: "13%",
        render: (_: unknown, record: ITask) =>
          !isClosed && hasAccessByController("Close") ? (
            <TableActions setTaskIdToClose={setTaskIdToClose} id={record.id} />
          ) : (
            <div />
          ),
      },
    ],
    [
      isClosed,
      onDownloadClick,
      setTaskIdToClose,
      hasAccessByController,
      reload,
    ],
  );

  return { columns };
};
